import React from 'react'
import PropTypes from 'prop-types'

// Project Components
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant'
import LoadingSpinner from '~/app/components/loading-spinner'
import ProductImage from './productImage'
import QuantityPicker from './quantityPicker'
import RemoveProductBtn from './removeProductBtn'
import ProductPrice from './productPrice'
import ProductName from '../../../../components/minicart/productName'

// Utilities
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

// Hooks
import {useCurrency, useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'

/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} product Product to be represented in the list item.
 * @param {func} handleRemoveItem callback function to be invoked when product is removed from the basket
 * @param {func} onItemQuantityChange callback function to be invoked whenever item quantity changes.
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @param {number} index Product index in the basket.productItems array
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const ProductItem = ({
    product,
    handleRemoveItem,
    onItemQuantityChange = noop,
    showLoading = false,
    index
}) => {
    const {stepQuantity, quantity, setQuantity} = useDerivedProduct(product)
    const {currency: activeCurrency} = useCurrency()

    return (
        <div className="relative mb-5" data-testid={`sf-cart-item-${product.productId}`}>
            <ItemVariantProvider variant={product} data-testid="productItem-ItemVariantProvider">
                {showLoading && <LoadingSpinner data-testid="productItem-LoadingSpinner" />}
                <div className="grid grid-cols-12 gap-2.5">
                    <div className="col-span-3">
                        <ProductImage data-testid="productItem-ProductImage" />
                    </div>
                    <div className="col-span-9 flex flex-col">
                        <ProductName data-testid="productItem-ProductName" />
                        <div className="grid grid-cols-12 gap-2.5">
                            <div className="col-span-5">
                                <ProductPrice
                                    product={product}
                                    currency={activeCurrency}
                                    data-testid="productItem-ProductPrice"
                                />
                            </div>
                            <div className="col-span-6">
                                <QuantityPicker
                                    step={stepQuantity}
                                    value={quantity}
                                    min={0}
                                    clampValueOnBlur={false}
                                    onBlur={(e) => {
                                        // Default to last known quantity if a user leaves the box with an invalid value
                                        const {value} = e.target

                                        if (!value) {
                                            setQuantity(product.quantity)
                                        }
                                    }}
                                    onChange={(stringValue, numberValue) => {
                                        // Set the Quantity of product to value of input if value number
                                        if (numberValue >= 0) {
                                            // Call handler
                                            onItemQuantityChange(numberValue).then(
                                                (isValidChange) =>
                                                    isValidChange && setQuantity(numberValue)
                                            )
                                        } else if (stringValue === '') {
                                            // We want to allow the use to clear the input to start a new input so here we set the quantity to '' so NAN is not displayed
                                            // User will not be able to add '' quantity to the cart due to the add to cart button enablement rules
                                            setQuantity(stringValue)
                                        }
                                    }}
                                    productName={product?.name}
                                    data-testid="productItem-QuantityPicker"
                                />
                            </div>
                            <div className="col-span-1 flex items-center">
                                <RemoveProductBtn
                                    productIndex={index}
                                    onRemoveItemClick={handleRemoveItem}
                                    data-testid="productItem-RemoveProductBtn"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </ItemVariantProvider>
        </div>
    )
}

ProductItem.propTypes = {
    product: PropTypes.object,
    handleRemoveItem: PropTypes.func,
    onItemQuantityChange: PropTypes.func,
    showLoading: PropTypes.bool,
    index: PropTypes.number.isRequired
}

export default ProductItem
